import { useCallback, useEffect, useState } from "react";
import { Plus, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { clp, fmtDate, OVERNIGHT, STATUS } from "@/lib/format";
import { Btn, PageTitle } from "@/components/admin/AdminUI";
import { ReservationDetail } from "@/components/admin/ReservationDetail";
import { ManualReservation } from "@/components/admin/ManualReservation";

const TABS = [["pendiente", "Pendientes"], ["confirmada", "Confirmadas"], ["", "Todas"], ["rechazada", "Rechazadas"], ["cancelada", "Canceladas"]];

export const StatusBadge = ({ s }) => (
  <span className={`inline-block rounded-full border px-2.5 py-0.5 text-xs font-semibold ${STATUS[s]?.cls}`} data-testid="status-badge">{STATUS[s]?.label || s}</span>
);

export default function AdminReservations() {
  const [status, setStatus] = useState("pendiente");
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(null);
  const [manual, setManual] = useState(false);
  const load = useCallback(() => {
    api.get("/admin/reservations", { params: { status: status || undefined, q: q || undefined } }).then((r) => setRows(r.data));
  }, [status, q]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  return (
    <div data-testid="admin-reservations-page">
      <PageTitle title="Reservas">
        <Btn variant="accent" onClick={() => setManual(true)} data-testid="new-manual-reservation-btn"><Plus className="h-4 w-4" />Reserva manual</Btn>
      </PageTitle>
      <div className="flex flex-col md:flex-row gap-3 md:items-center mb-6">
        <div className="flex gap-1 overflow-x-auto rounded-full bg-[#F3EDE2] p-1">
          {TABS.map(([k, l]) => (
            <button key={k || "all"} onClick={() => setStatus(k)} data-testid={`filter-status-${k || "todas"}`}
              className={`rounded-full px-4 py-2 text-sm font-medium whitespace-nowrap ${status === k ? "bg-[#5E6B52] text-white" : "hover:bg-white"}`}>{l}</button>
          ))}
        </div>
        <div className="relative md:ml-auto md:w-72">
          <Search className="h-4 w-4 absolute left-3 top-3 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Código, nombre, RUT, teléfono…" className="pl-9 bg-white" data-testid="reservations-search-input" />
        </div>
      </div>
      <div className="rounded-2xl border border-border bg-white overflow-x-auto">
        <table className="w-full text-sm" data-testid="reservations-table">
          <thead className="bg-[#F3EDE2]/60 text-left text-xs uppercase tracking-wider text-muted-foreground">
            <tr>{["Código", "Cliente", "Experiencia", "Fechas", "Pers.", "Total", "Abono", "Saldo", "Estado", ""].map((h) => <th key={h} className="px-4 py-3 font-semibold whitespace-nowrap">{h}</th>)}</tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-border hover:bg-[#FAF7F2] cursor-pointer" onClick={() => setOpen(r.id)} data-testid={`reservation-row-${r.code}`}>
                <td className="px-4 py-3 font-mono font-semibold whitespace-nowrap">{r.code}</td>
                <td className="px-4 py-3 whitespace-nowrap">{r.customer?.nombre} {r.customer?.apellido}<p className="text-xs text-muted-foreground">{r.customer?.telefono}</p></td>
                <td className="px-4 py-3">{r.experience_name}{r.extras?.length > 0 && <p className="text-xs text-muted-foreground">+{r.extras.length} extra(s)</p>}</td>
                <td className="px-4 py-3 whitespace-nowrap">{fmtDate(r.check_in)}{OVERNIGHT.includes(r.experience) && <> → {fmtDate(r.check_out)}</>}</td>
                <td className="px-4 py-3">{r.guests}</td>
                <td className="px-4 py-3 tabular-nums">{clp(r.total)}</td>
                <td className="px-4 py-3 tabular-nums">{clp(r.deposit)}</td>
                <td className="px-4 py-3 tabular-nums">{clp(r.balance)}</td>
                <td className="px-4 py-3"><StatusBadge s={r.status} /></td>
                <td className="px-4 py-3 text-[#5E6B52] font-semibold">Ver</td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan={10} className="px-4 py-10 text-center text-muted-foreground" data-testid="reservations-empty">No hay reservas en esta vista.</td></tr>}
          </tbody>
        </table>
      </div>
      {open && <ReservationDetail id={open} onClose={() => setOpen(null)} onChanged={load} />}
      <ManualReservation open={manual} onOpenChange={setManual} onCreated={load} />
    </div>
  );
}
